import type {BroadcastFn} from '@backstage-components/base';
import {assign, createMachine} from 'xstate';
import {
  type InstructionSchema,
  PublicAccessCodeInstructionSchema,
} from './PublicAccessCodeDefinition';

export const PublicAccessCodeMachine = createMachine<
  Context,
  InstructionSchema,
  Typestate
>(
  {
    id: 'PublicAccessCode',
    initial: 'idle',
    states: {
      idle: {
        on: {
          'PublicAccessCode:verify': {target: 'pending'},
        },
      },
      pending: {
        entry: ['stashPasscode'],
        on: {
          'PublicAccessCode:success': {
            target: 'success',
            actions: ['updateAttendee'],
          },
          'PublicAccessCode:failure': {
            target: 'failure',
            actions: ['updateReason'],
          },
        },
      },
      success: {
        entry: ['broadcastSuccess'],
        on: {
          'PublicAccessCode:verify': {target: 'pending'},
        },
      },
      failure: {
        entry: ['broadcastFailure', 'clearAttendeeId'],
        on: {
          'PublicAccessCode:reset': 'idle',
          'PublicAccessCode:verify': {target: 'pending'},
        },
      },
    },
  },
  {
    actions: {
      broadcastFailure: (context, event) => {
        if (event.type === 'PublicAccessCode:failure') {
          context.broadcast({
            type: 'PublicAccessCode:on-failure',
            meta: {
              showId: context.showId,
              reason: event.meta.reason,
            },
          });
        }
      },
      broadcastSuccess: (context, event) => {
        if (event.type === 'PublicAccessCode:success') {
          if (typeof window === 'object') {
            const e: PublicAccessCodeSuccessEvent = new CustomEvent(
              PublicAccessCodeSuccessEventName,
              {
                detail: {
                  attendee: event.meta.attendee,
                  passCode: context.passCode,
                  showId: context.showId,
                },
              }
            );
            document.body.dispatchEvent(e);
          }
        }
      },
      clearAttendeeId: assign((context, event) => {
        if (event.type === 'PublicAccessCode:failure') {
          // Return `BaseContext`
          return {
            attendee: undefined,
            broadcast: context.broadcast,
            showId: context.showId,
          };
        } else {
          return context;
        }
      }),
      stashPasscode: assign((context, event) => {
        if (event.type === 'PublicAccessCode:verify') {
          return {
            ...context,
            passCode: event.meta.passCode,
          };
        } else {
          return context;
        }
      }),
      updateAttendee: assign((context, event) => {
        if (event.type === 'PublicAccessCode:success') {
          const attendee = event.meta.attendee;
          const token = attendee.chatTokens.find(
            (token) => token.token.length > 0
          )?.token;
          return {
            broadcast: context.broadcast,
            showId: context.showId,
            attendee,
            passCode: context.passCode,
            token,
          };
        } else {
          return context;
        }
      }),
      updateReason: assign((context, event) => {
        if (event.type === 'PublicAccessCode:failure') {
          return {
            broadcast: context.broadcast,
            showId: context.showId,
            reason: event.meta.reason,
          };
        } else {
          return context;
        }
      }),
    },
  }
);

interface BaseContext {
  broadcast: BroadcastFn<typeof PublicAccessCodeInstructionSchema>;
  showId: string;
  attendee?: Attendee;
  passCode?: string;
}

interface FailureContext extends BaseContext {
  reason?: string;
}

interface SuccessContext extends BaseContext {
  attendee: Attendee;
}

type Context = SuccessContext | BaseContext;

type Typestate =
  | {value: 'idle'; context: BaseContext}
  | {value: 'pending'; context: BaseContext}
  | {value: 'success'; context: SuccessContext}
  | {value: 'failure'; context: FailureContext};

interface Attendee {
  id: string;
  email: string | null;
  name: string;
  chatTokens: ChatToken[];
  tags: string[];
}

interface ChatToken {
  token: string;
}

export const PublicAccessCodeSuccessEventName = 'PublicAccessCode:success';

export type PublicAccessCodeSuccessEvent = CustomEvent<
  Pick<SuccessContext, 'attendee' | 'passCode' | 'showId'>
>;
